import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { exportedOnText } from './stamp';

// ─────────────────────────────────────────────
// Full-page (visual) PDF export
//
// Captures the dashboard content to an A4 PDF. Instead of rendering the whole
// element to ONE tall image and slicing it by raw page height (which cut a
// chart or table in half across the page boundary), it captures each top-level
// SECTION separately and places them one after another, starting a NEW page
// whenever the next section would not fit on the current one. So a chart/table
// is never split across pages. Used for the "Export PDF" button where the user
// wants a snapshot of exactly what is on screen.
// ─────────────────────────────────────────────

export interface PagePdfOptions {
  /** The element to capture (e.g. the dashboard <main>). */
  element: HTMLElement;
  /** Output file name (without extension). */
  fileName?: string;
  /** Page orientation. Defaults to portrait. */
  orientation?: 'portrait' | 'landscape';
}

const A4 = {
  portrait: { width: 210, height: 297 },
  landscape: { width: 297, height: 210 },
} as const;

const MARGIN = 8; // mm page margin
const FOOTER_H = 6; // mm reserved at the bottom for the "Exported on" caption

/**
 * The blocks to paginate at. Each is treated as atomic — never split across a
 * page. Pages mark their report rows/charts with `.dash-section`; fall back to
 * the element's direct children when no sections are marked.
 */
function collectSections(element: HTMLElement): HTMLElement[] {
  const marked = Array.from(
    element.querySelectorAll<HTMLElement>('.dash-section')
  );
  if (marked.length > 0) return marked;
  return Array.from(element.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement
  );
}

/**
 * Render a DOM element to a multi-page A4 PDF and trigger a download, breaking
 * pages at section boundaries so no single section is cut in half.
 */
export async function exportElementToPdf({
  element,
  fileName = 'dashboard',
  orientation = 'portrait',
}: PagePdfOptions): Promise<void> {
  const page = A4[orientation];
  const usableWidth = page.width - MARGIN * 2;
  const usableHeight = page.height - MARGIN * 2 - FOOTER_H;

  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  const captureOpts = {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  } as const;

  const sections = collectSections(element);

  // Fallback: if there are no sensible sections, capture the whole element and
  // slice it (previous behaviour) so we still produce a usable PDF.
  const toCapture =
    sections.length > 0 ? sections : ([element] as HTMLElement[]);

  let cursorY = MARGIN;
  let pageHasContent = false;

  const addFooter = () => {
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(exportedOnText(), MARGIN, page.height - MARGIN);
  };

  for (const section of toCapture) {
    // Skip zero-size / hidden sections.
    if (section.offsetWidth === 0 || section.offsetHeight === 0) continue;

    const canvas = await html2canvas(section, {
      ...captureOpts,
      windowWidth: section.scrollWidth,
      windowHeight: section.scrollHeight,
    });
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = usableWidth;
    let imgHeight = (canvas.height * imgWidth) / canvas.width;

    // A single section taller than a full page can't avoid being split — it is
    // scaled down to fit one page height so it still renders whole.
    if (imgHeight > usableHeight) {
      imgHeight = usableHeight;
    }

    // Not enough room left on this page → start a new one.
    if (pageHasContent && cursorY + imgHeight > MARGIN + usableHeight) {
      addFooter();
      doc.addPage();
      cursorY = MARGIN;
      pageHasContent = false;
    }

    const drawWidth = (canvas.width * imgHeight) / canvas.height;
    const x = MARGIN + Math.max(0, (usableWidth - drawWidth) / 2);
    doc.addImage(imgData, 'PNG', x, cursorY, drawWidth, imgHeight);
    cursorY += imgHeight + 4; // 4mm gap between sections
    pageHasContent = true;
  }

  addFooter();
  doc.save(`${fileName}.pdf`);
}
